import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card } from 'reactstrap';

import { getStatus } from '../../api/requests/tuitionContinuityV2';
import Base from '../../components/base/Base';
import CareerSelector from '../../components/careerSelector/CareerSelector';
import { CareerStatus } from '../../components/careerSelector/academicInformationTypes';
import { CareerSelectorConsumer } from '../../components/careerSelector/parts/CareerSelectorProvider';
import DisplayError from '../../components/info/DisplayError';
import Loading from '../../components/info/Loading';
import TopBar from '../../components/topBar/TopBar';
import {
  ContinuityEventType,
  ContinuityStatusResponse,
} from '../../types/tuitionContinuityV2';
import { TuitionContinuityV2Content } from '../tuitionContinuityV2/TuitionContinuityV2';
import GoToTuitionMsg from './parts/GoToTuitionMsg';
import TuitionContinuityLoader from './parts/TuitionContinuityLoader';

/**
 * Shared entry point for tuition continuity.
 * Loads status once, then renders V1 (legacy) or V2 content based on eventType.
 */
function VersionRouter({
  studyPlanEnrollmentId,
  goToTuition,
}: {
  studyPlanEnrollmentId: number;
  goToTuition: (studyPlanEnrollmentId: number) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusData, setStatusData] = useState<ContinuityStatusResponse | null>(
    null,
  );
  const [isV2, setIsV2] = useState<boolean | null>(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getStatus(studyPlanEnrollmentId, 0);
    if (res.error) {
      setError(res.error.code);
      setLoading(false);
      return;
    }
    if (!res.data) {
      setLoading(false);
      return;
    }

    const v2 =
      res.data.eventType === ContinuityEventType.ORDINARY ||
      res.data.eventType === ContinuityEventType.EXTRAORDINARY;

    setStatusData(res.data);
    setIsV2(v2);
    setLoading(false);
  }, [studyPlanEnrollmentId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (error) {
    return (
      <DisplayError
        insideCard
        body={error}
        retryAction={loadStatus}
        loadingAction={loading}
      />
    );
  }

  if (loading || isV2 === null) {
    return <Loading insideCard />;
  }

  if (isV2 && statusData) {
    return (
      <TuitionContinuityV2Content
        studyPlanEnrollmentId={studyPlanEnrollmentId}
        statusData={statusData}
      />
    );
  }

  return (
    <TuitionContinuityLoader studyPlanEnrollmentId={studyPlanEnrollmentId}>
      {({ data: studentStatus }) => (
        <GoToTuitionMsg {...studentStatus} goToTuition={goToTuition} />
      )}
    </TuitionContinuityLoader>
  );
}

export default function TuitionContinuity() {
  const { t } = useTranslation();

  const history = useHistory();

  const goToTuition = useCallback(
    (studyPlanEnrollmentId: number) => {
      history.push(`tuition-continuity/tuition/step/${studyPlanEnrollmentId}`);
    },
    [history],
  );

  return (
    <Base>
      <TopBar title={t('tuitionContinuity.title')} />
      <Card className="p-4 mx-3">
        <CareerSelector
          allowedStatus={[CareerStatus.Regular]}
          excludeContinuousEducation
        >
          <CareerSelectorConsumer>
            {({ selectedCareer }) => (
              <VersionRouter
                studyPlanEnrollmentId={selectedCareer.studyPlanEnrollmentId}
                goToTuition={goToTuition}
              />
            )}
          </CareerSelectorConsumer>
        </CareerSelector>
      </Card>
    </Base>
  );
}
